import { useRef, useEffect, useContext, useMemo, useState } from 'react';
// Preserve Mapbox's shared main-thread/worker bundle through CRA's Babel pipeline.
// https://docs.mapbox.com/mapbox-gl-js/guides/transpiling/
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useApi } from '../hooks/useApi';
import { Merlin } from '../utils/api';
import {
  ChartCard, ChartLoader, ChartError, LineChart, BarChart, DonutChart,
} from '../components/Charts';
import { ThemeContext } from '../context/ThemeContext';
import { PALETTE, COLORS, fmtNum } from '../utils/theme';
import { geoName } from '../utils/postcodes';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN
  || 'pk.eyJ1IjoidmlrYXNoLWV1cmVrYSIsImEiOiJjbXIzZWF5cXcwMGZwMndzZHRkZ2k4dXptIn0.SbeHHlrQ3ZlxfgoSXcEL7g';

// ── Merlin attractions with Google Maps coordinates + online-data brand ─────────
// `online` = brand name in the merlin_online_* tables (null = no online data).
const MERLIN_SITES = [
  { brand: 'Alton Towers Theme Park', online: 'Alton Towers Theme Park', name: 'Alton Towers',            lat: 52.9880, lng: -1.8920 },
  { brand: 'Chessington',             online: 'Chessington',             name: 'Chessington World of Adventures', lat: 51.3489, lng: -0.3186 },
  { brand: 'London Eye',              online: null,                      name: 'London Eye',              lat: 51.5033, lng: -0.1196 },
  { brand: 'Madame Tussauds',         online: 'Madame Tussauds',         name: 'Madame Tussauds London',  lat: 51.5230, lng: -0.1547 },
  { brand: 'Thorpe Park',             online: 'Thorpe Park',             name: 'Thorpe Park',             lat: 51.4045, lng: -0.5100 },
  { brand: 'Warwick Castle',          online: 'Warwick Castle',          name: 'Warwick Castle',          lat: 52.2785, lng: -1.5850 },

  { brand: 'Legoland UK', online: 'Legoland UK', name: 'Legoland Windsor',                     lat: 51.4655, lng: -0.6412 },
  { brand: 'Legoland UK', online: 'Legoland UK', name: 'Legoland Discovery Centre Manchester', lat: 53.4677, lng: -2.3450 },
  { brand: 'Legoland UK', online: 'Legoland UK', name: 'Legoland Discovery Centre Birmingham', lat: 52.4790, lng: -1.9160 },

  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'Sea Life London',                 lat: 51.5016, lng: -0.1195 },
  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'National Sea Life Centre, Birmingham', lat: 52.4796, lng: -1.9089 },
  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'Sea Life Blackpool',              lat: 53.8107, lng: -3.0553 },
  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'Sea Life Brighton',               lat: 50.8195, lng: -0.1357 },
  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'Sea Life Manchester (Trafford)',  lat: 53.4676, lng: -2.3436 },
  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'Sea Life Great Yarmouth',         lat: 52.5730, lng:  1.7305 },
  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'Sea Life Loch Lomond',            lat: 56.0030, lng: -4.5830 },
  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'Sea Life Scarborough',            lat: 54.2980, lng: -0.4340 },
  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'Sea Life Weymouth',               lat: 50.6155, lng: -2.4360 },
  { brand: 'Sea Life', online: 'Visit Sea Life', name: 'Sea Life Hunstanton',             lat: 52.9430, lng:  0.4890 },
];

const GENDER_LABEL = { M: 'Male', F: 'Female', U: 'Unknown' };
const MONTHS = [
  { id: '2026-07', label: 'Jul 2026' },
  { id: '2026-08', label: 'Aug 2026' },
  { id: 'all',     label: 'Jul–Aug' },
];

// ── Brand online-behaviour dashboard (shown on pin click) ───────────────────────
function BrandDashboard({ selected, month, setMonth }) {
  const { data, loading, error } = useApi(
    () => Merlin.online(selected.online, month),
    { brand: selected.online, month },
    !!selected.online,
  );
  const d = data?.[0];

  if (!selected.online) {
    return <ChartCard title={selected.name}><div className="empty-state">No online-behaviour data available for this attraction.</div></ChartCard>;
  }

  const kpis = d?.kpis || {};
  const daily = d?.daily || [];

  return (
    <ChartCard
      title={`Online Behaviour — ${selected.online}`}
      sub={`Website visitors · ${selected.name}`}
      action={
        <div className="dj-metric-toggle">
          {MONTHS.map(m => (
            <button key={m.id} className={`dj-metric-btn ${month === m.id ? 'active' : ''}`} onClick={() => setMonth(m.id)}>
              {m.label}
            </button>
          ))}
        </div>
      }
    >
      {loading && <ChartLoader height={300} />}
      {error && <ChartError message={error} height={300} />}
      {!loading && !error && d && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
            <div className="kpi-card" style={{ background: COLORS.teal, borderColor: COLORS.teal }}>
              <div className="kpi-label" style={{ color: 'rgba(255,255,255,0.85)' }}>Total Visitors</div>
              <div className="kpi-value" style={{ color: '#fff' }}>{fmtNum(kpis.total_visitors)}</div>
              <div className="kpi-delta neutral" style={{ color: 'rgba(255,255,255,0.75)' }}>visitor-days · {selected.online}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Total Page Events</div>
              <div className="kpi-value">{fmtNum(kpis.total_events)}</div>
              <div className="kpi-delta neutral">{kpis.avg_events_per_visitor} events / visitor</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Avg Time / Visit</div>
              <div className="kpi-value">{kpis.avg_seconds_per_visit != null ? `${kpis.avg_seconds_per_visit}s` : '—'}</div>
              <div className="kpi-delta neutral">seconds per event</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Peak Day</div>
              <div className="kpi-value" style={{ fontSize: 20 }}>{kpis.peak_day ? kpis.peak_day.slice(5) : '—'}</div>
              <div className="kpi-delta neutral">{fmtNum(kpis.peak_visitors)} visitors · {kpis.active_days} active days</div>
            </div>
          </div>

          {/* Daily trend */}
          <ChartCard title="Daily Website Visitors" style={{ marginBottom: 16 }}>
            <LineChart
              categories={daily.map(r => r.day.slice(5))}
              series={[{ name: 'Visitors', color: COLORS.teal, data: daily.map(r => r.visitors), area: true }]}
              height={260}
            />
          </ChartCard>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <ChartCard title="By Age Band">
              <BarChart
                categories={(d.by_age || []).map(r => r.age_band)}
                series={[{ name: 'Visitors', color: COLORS.indigo, data: (d.by_age || []).map(r => r.visitors) }]}
                height={260}
              />
            </ChartCard>
            <ChartCard title="By Gender">
              <DonutChart
                data={(d.by_gender || []).map((r, i) => ({ name: GENDER_LABEL[r.gender] || r.gender, value: r.visitors, color: PALETTE[i] }))}
                height={260}
              />
            </ChartCard>
            <ChartCard title="By Funnel Stage (alpha)">
              <DonutChart
                data={(d.by_alpha || []).map((r, i) => ({ name: r.alpha, value: r.visitors, color: PALETTE[i] }))}
                height={260}
              />
            </ChartCard>
          </div>

          <ChartCard title={`Top Postcode Areas (${(d.by_postcode || []).length})`}>
            <BarChart
              categories={(d.by_postcode || []).map(r => geoName(r.postcode_area))}
              series={[{ name: 'Visitors', color: COLORS.blue, data: (d.by_postcode || []).map(r => r.visitors) }]}
              horizontal
              height={Math.max(280, (d.by_postcode || []).length * 26 + 60)}
            />
          </ChartCard>
        </>
      )}
    </ChartCard>
  );
}

export default function PinpointIntelligenceView() {
  const { dark } = useContext(ThemeContext);
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const markersRef   = useRef([]);

  const [selected, setSelected] = useState(null);   // { name, brand, online }
  const [month,    setMonth]    = useState('2026-07');
  const [mapErr,   setMapErr]   = useState(null);
  const dashRef = useRef(null);

  // Scroll to the dashboard when a pin is clicked
  useEffect(() => {
    if (selected) requestAnimationFrame(() => dashRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [selected]);

  const brands = useMemo(() => [...new Set(MERLIN_SITES.map(s => s.brand))], []);
  const colorFor = useMemo(
    () => Object.fromEntries(brands.map((b, i) => [b, PALETTE[i % PALETTE.length]])),
    [brands],
  );

  useEffect(() => {
    let map;
    const raf = requestAnimationFrame(() => {
      try {
        if (!mapboxgl.supported || !mapboxgl.supported()) {
          setMapErr('This browser does not support Mapbox GL (WebGL).');
          return;
        }
        mapboxgl.accessToken = MAPBOX_TOKEN;
        map = new mapboxgl.Map({
          container: containerRef.current,
          style: dark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
          center: [-2.6, 54.2],
          zoom: 5,
        });
      } catch (e) {
        setMapErr(`Map init failed: ${e?.message || e}`);
        return;
      }
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      mapRef.current = map;

      map.on('error', e => setMapErr(e?.error?.message || 'Mapbox error (see console)'));
      // Container can be 0-size at init (esp. in prod) → force a resize once ready
      map.on('load', () => { map.resize(); });
      setTimeout(() => { try { map.resize(); } catch { /* ignore */ } }, 400);

      map.on('load', () => {
        const bounds = new mapboxgl.LngLatBounds();
        MERLIN_SITES.forEach(site => {
          const popup = new mapboxgl.Popup({ offset: 24, closeButton: false })
            .setLngLat([site.lng, site.lat])
            .setHTML(`<strong>${site.name}</strong><br/><span style="color:#64748b">${site.brand}</span>`);
          const marker = new mapboxgl.Marker({ color: colorFor[site.brand] })
            .setLngLat([site.lng, site.lat])
            .addTo(map);
          const el = marker.getElement();
          el.style.cursor = 'pointer';
          el.addEventListener('mouseenter', () => popup.addTo(map));
          el.addEventListener('mouseleave', () => popup.remove());
          el.addEventListener('click', () => setSelected({ name: site.name, brand: site.brand, online: site.online }));
          markersRef.current.push(marker);
          bounds.extend([site.lng, site.lat]);
        });
        map.fitBounds(bounds, { padding: 60, maxZoom: 8, duration: 0 });
      });
    });

    return () => {
      cancelAnimationFrame(raf);
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      if (map) map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const themeInit = useRef(false);
  useEffect(() => {
    if (!themeInit.current) { themeInit.current = true; return; }
    const map = mapRef.current;
    if (map) map.setStyle(dark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11');
  }, [dark]);

  return (
    <div>
      <ChartCard
        title="Pinpoint Intelligence — Merlin Attractions"
        sub={`${MERLIN_SITES.length} UK sites across ${brands.length} Merlin brands · click a pin for online behaviour`}
      >
        <div style={{ position: 'relative' }}>
          <div ref={containerRef} style={{ width: '100%', height: 620, borderRadius: 8, overflow: 'hidden', background: 'var(--bg-page)' }} />
          {mapErr && (
            <div style={{
              position: 'absolute', top: 12, right: 12, zIndex: 2, maxWidth: 360,
              background: 'var(--bg-card)', border: '1px solid var(--negative)',
              color: 'var(--negative)', borderRadius: 8, padding: '10px 12px',
              fontSize: 12, boxShadow: 'var(--shadow)',
            }}>
              <strong>Map error:</strong> {mapErr}
            </div>
          )}
          <div style={{
            position: 'absolute', top: 12, left: 12, zIndex: 1,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '10px 12px', boxShadow: 'var(--shadow)',
            fontSize: 12, maxHeight: 'calc(100% - 24px)', overflowY: 'auto',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Merlin brands</div>
            {brands.map(b => (
              <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '2px 0' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: colorFor[b], flexShrink: 0 }} />
                <span style={{ color: 'var(--text-secondary)' }}>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </ChartCard>

      {selected && (
        <div ref={dashRef} style={{ marginTop: 16, scrollMarginTop: 12 }}>
          <BrandDashboard selected={selected} month={month} setMonth={setMonth} />
        </div>
      )}
    </div>
  );
}
